import { type Asset as BaseAsset } from "@mg/schemas/src/commons/asset";
import {
  type EnterpriseAskPunttSkill,
  type EnterpriseAskPunttConnector,
  type EnterpriseCustomWorkflow,
} from "@mg/schemas/src/commons/enterprise";
import {
  Spinner,
  Trash,
  Plus,
  File as FileIcon,
  TextAa as FontIcon,
  FileDoc,
  FilePdf,
  FileText,
  FileVideo,
  FilePpt,
  Link,
  X,
  Check,
  DownloadSimple,
  Question,
  PencilSimple,
} from "@phosphor-icons/react";
import {
  Button,
  Checkbox,
  Dialog,
  Flex,
  Grid,
  Heading,
  Separator,
  Switch,
  Table,
  Tabs,
  Text,
  TextArea,
  TextField,
  Tooltip,
} from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { createRoute, redirect } from "@tanstack/react-router";
import cx from "classnames";
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Preview } from "../../../components/Preview";
import { isFlagOn } from "../../../config/featureFlags";
import { useUI } from "../../../contexts/ui";
import { getGuidelines } from "../../../services/brand-guidelines";
import { type SkillDocument } from "../../../services/skill-documents";
import { uploadFile } from "../../../services/upload";
import { useAnalytics } from "../../../utils/analytics";
import { personaName, requiresAuth } from "../../../utils/auth";
import { renderPersonaAvatar } from "../../../utils/helpers/ai-persona-avatars";
import { downloadAsset } from "../../../utils/http";
import {
  useUpdatePersonaMutation,
  useUpdateAskPunttSkillMutation,
  useUpdateAskPunttEnabledMutation,
  useUpdateAskPunttConnectorMutation,
  useCreateCustomWorkflowMutation,
  useUpdateCustomWorkflowMutation,
  useDeleteCustomWorkflowMutation,
} from "../../../utils/queries/knowledge-db";
import { useUpdateGuidelinesMutation } from "../../../utils/queries/projects";
import { isValidUrl } from "../../../utils/validation";
import { RequiredInputsEditor } from "../../ask-puntt/components/RequiredInputsEditor";
import { SkillDocumentsPanel } from "../../ask-puntt/components/SkillDocumentsPanel";
import { authLayoutRoute } from "../../auth-layout/route";
import { ticketsRoute } from "../../tickets/route";

interface AssetWithUrl extends BaseAsset {
  publicUrl?: string;
  name?: string;
}

export const knowledgeSettingRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "knowledge-settings",
  beforeLoad() {
    if (!isFlagOn("manage-brand-guidelines")) {
      throw redirect({
        to: ticketsRoute.to,
      });
    }

    requiresAuth();
  },
  component: KnowledgeSettings,
});

function KnowledgeSettings() {
  const { notify } = useUI();
  const query = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const mutation = useUpdateGuidelinesMutation();
  const updatePersonaMutation = useUpdatePersonaMutation();
  const updateAskPunttSkillMutation = useUpdateAskPunttSkillMutation();
  const updateAskPunttEnabledMutation = useUpdateAskPunttEnabledMutation();
  const updateAskPunttConnectorMutation = useUpdateAskPunttConnectorMutation();
  const createWorkflowMutation = useCreateCustomWorkflowMutation();
  const updateWorkflowMutation = useUpdateCustomWorkflowMutation();
  const deleteWorkflowMutation = useDeleteCustomWorkflowMutation();
  const posthog = useAnalytics();

  const handleSkillUpdate = useCallback(
    async (payload: {
      skillId: string;
      enabled?: boolean;
      customInstructions?: string;
    }) => {
      await updateAskPunttSkillMutation.mutateAsync(payload);
      notify({
        title: "Skill settings updated",
        variant: "success",
      });
    },
    [updateAskPunttSkillMutation, notify],
  );

  const handleConnectorUpdate = useCallback(
    async (payload: { connectorId: string; enabled?: boolean }) => {
      await updateAskPunttConnectorMutation.mutateAsync(payload);
      notify({
        title: "Connector settings updated",
        variant: "success",
      });
    },
    [updateAskPunttConnectorMutation, notify],
  );

  const handleAskPunttEnabledChange = useCallback(
    async (enabled: boolean) => {
      await updateAskPunttEnabledMutation.mutateAsync({ enabled });
      notify({
        title: enabled
          ? "AskPuntt enabled for your organization"
          : "AskPuntt disabled for your organization",
        variant: "success",
      });
    },
    [updateAskPunttEnabledMutation, notify],
  );

  const [guidelines, setGuidelines] = useState("");
  const [files, setFiles] = useState<AssetWithUrl[]>([]);
  const [templates, setTemplates] = useState<AssetWithUrl[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingTemplates, setIsUploadingTemplates] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [editingUrlForFile, setEditingUrlForFile] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (query.data?.guidelines) {
      setGuidelines(query.data.guidelines.description as string);
      setFiles((query.data.guidelines.files ?? []) as []);
      setTemplates((query.data.guidelines.templates ?? []) as []);
    }
  }, [query.data]);

  const handleInput = async (e: ChangeEvent<HTMLTextAreaElement>) => {
    setGuidelines(e.target.value);
    setIsSaved(false);
    setIsTyping(true);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setIsTyping(false);
        await mutation.mutateAsync(
          {
            description: e.target.value,
            files: files,
            templates: templates,
          },
          {
            onSuccess() {
              setIsSaved(true);
              setTimeout(() => setIsSaved(false), 2000);
              posthog.capture("guidelines-updated", {
                description: e.target.value,
                files: files,
              });
            },
          },
        );
      } catch (error) {
        notify({
          title: "Error saving guidelines",
          message: String(error),
          variant: "error",
        });
      }
    }, 2000);
  };

  function getFileIcon(fileName: string) {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return <FilePdf size={20} className="min-w-5" />;
      case "doc":
      case "docx":
        return <FileDoc size={20} className="min-w-5" />;
      case "txt":
        return <FileText size={20} className="min-w-5" />;
      case "mp4":
      case "mov":
      case "avi":
        return <FileVideo size={20} className="min-w-5" />;
      case "ppt":
      case "pptx":
        return <FilePpt size={20} className="min-w-5" />;
      case "ttf":
      case "otf":
      case "woff":
      case "woff2":
        return <FontIcon size={20} className="min-w-5" />;
      default:
        return <FileIcon size={20} className="min-w-5" />;
    }
  }

  const handleGuidelineUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileList = event?.target?.files;

    if (fileList?.[0]) {
      setIsUploading(true);
      const startTime = performance.now();

      try {
        const payloads = await Promise.all(
          Array.from(fileList).map((file) => uploadFile(file)),
        );

        const assets = await Promise.all(
          payloads.map(async (payload, i) => {
            const key = payload.key;

            return {
              source: "https://static.puntt.ai/" + key,
              originalSource: fileList[i]?.name,
              type: (fileList[i]?.type?.includes("image")
                ? "image"
                : "file") as AssetWithUrl["type"],
            };
          }),
        );

        const newFiles = [...files, ...assets];
        setFiles(newFiles);
        posthog.capture("guidelines-file-uploaded", {
          durationMs: performance.now() - startTime,
          files: assets,
        });

        mutation.mutate(
          {
            description: guidelines,
            files: newFiles as AssetWithUrl[],
            templates: templates,
          },
          {
            onSuccess() {
              posthog.capture("guidelines-updated", {
                description: guidelines,
                files: newFiles,
              });
              notify({
                title: "Guidelines Updated",
                message: "",
                variant: "success",
              });
            },
          },
        );
        if (fileInputRef?.current) {
          fileInputRef.current.value = "";
        }
      } catch (error) {
        notify({
          title: "Error uploading file",
          message: String(error),
          variant: "error",
        });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleTemplateUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const fileList = event?.target?.files;

    if (fileList?.[0]) {
      setIsUploadingTemplates(true);
      const startTime = performance.now();

      try {
        const payloads = await Promise.all(
          Array.from(fileList).map((file) => uploadFile(file)),
        );

        const assets = await Promise.all(
          payloads.map(async (payload, i) => {
            const key = payload.key;

            return {
              source: "https://static.puntt.ai/" + key,
              originalSource: fileList[i]?.name,
              type: (fileList[i]?.type?.includes("image")
                ? "image"
                : "file") as AssetWithUrl["type"],
            };
          }),
        );

        const newTemplates = [...templates, ...assets];
        setTemplates(newTemplates);
        posthog.capture("guidelines-template-uploaded", {
          durationMs: performance.now() - startTime,
          files: assets,
        });

        mutation.mutate(
          {
            description: guidelines,
            templates: newTemplates as AssetWithUrl[],
            files: files,
          },
          {
            onSuccess() {
              posthog.capture("guidelines-updated", {
                description: guidelines,
                templates: newTemplates,
              });
              notify({
                title: "Guidelines Updated",
                message: "",
                variant: "success",
              });
            },
          },
        );
        if (templateInputRef?.current) {
          templateInputRef.current.value = "";
        }
      } catch (error) {
        notify({
          title: "Error uploading file",
          message: String(error),
          variant: "error",
        });
      } finally {
        setIsUploadingTemplates(false);
      }
    }
  };

  const handleDelete = (file: { source: string }) => {
    const newFiles = files.filter(
      (f: AssetWithUrl) => f.source !== file.source,
    );
    setFiles(newFiles);
    mutation.mutate(
      {
        description: guidelines,
        files: newFiles,
        templates: templates,
      },
      {
        onSuccess() {
          notify({
            title: "File removed",
            message: "",
            variant: "success",
          });
        },
        onError() {
          setFiles(files);
          notify({
            title: "Error removing file",
            message: "Please try again",
            variant: "error",
          });
        },
      },
    );
  };

  const handleDeleteTemplate = (file: { source: string }) => {
    const newTemplates = templates.filter(
      (f: AssetWithUrl) => f.source !== file.source,
    );
    setTemplates(newTemplates);
    mutation.mutate(
      {
        description: guidelines,
        templates: newTemplates,
        files: files,
      },
      {
        onSuccess() {
          notify({
            title: "File removed",
            message: "",
            variant: "success",
          });
        },
        onError() {
          setTemplates(templates);
          notify({
            title: "Error removing file",
            message: "Please try again",
            variant: "error",
          });
        },
      },
    );
  };

  // Helper function to get file extension from either originalSource or source
  const getFileExtension = (file: AssetWithUrl) => {
    const fileName = file.originalSource || file.source;
    return fileName.split(".").pop()?.toLowerCase() || "";
  };

  interface AddUrlProps {
    file: AssetWithUrl;
    files: AssetWithUrl[];
    editingUrlForFile: string | null;
    setEditingUrlForFile: (url: string | null) => void;
    startingUrl: string;
    onSave: (files: AssetWithUrl[]) => void;
  }

  function AddUrlButton({
    file,
    files,
    editingUrlForFile,
    setEditingUrlForFile,
    startingUrl,
    onSave,
  }: AddUrlProps) {
    const [urlInput, setUrlInput] = useState(startingUrl);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          formRef.current &&
          !formRef.current.contains(event.target as Node)
        ) {
          setEditingUrlForFile(null);
        }
      }

      if (editingUrlForFile === file.source) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [editingUrlForFile, file.source, setEditingUrlForFile]);

    return (
      <Button
        variant="ghost"
        onClick={() => {
          if (editingUrlForFile !== file.source) {
            setEditingUrlForFile(file.source);
            setUrlInput(file.publicUrl || "");
          }
        }}
        className="ml-2 h-4 text-puntt-blue-12 hover:text-puntt-blue-11"
      >
        <Flex align="center" gap="1" className="h-full">
          {editingUrlForFile !== file.source && <Link size={16} />}
          <Text size="2" className="flex h-full items-center">
            {editingUrlForFile === file.source ? (
              <form
                ref={formRef}
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  const input = e.currentTarget.querySelector("input");
                  if (input?.validity.valid) {
                    const newFiles = files.map((f) => {
                      if (f.source === file.source) {
                        return { ...f, publicUrl: urlInput };
                      }
                      return f;
                    });
                    onSave(newFiles);
                  }
                }}
                className="flex items-center"
              >
                <div className="relative flex items-center self-stretch bg-puntt-blue-1">
                  <Tooltip
                    className="bg-puntt-blue-dark-7"
                    side="bottom"
                    content={
                      !urlInput || urlInput.match(/^https?:\/\/.*$/)
                        ? "Press Enter or click Check to save the public URL where this file can be accessed, or click X or outside the input to cancel"
                        : "Please enter a valid URL starting with http:// or https://"
                    }
                  >
                    <TextField.Root
                      type="url"
                      value={urlInput}
                      onChange={(e) => setUrlInput(e.target.value)}
                      placeholder="Enter URL"
                      className="w-[400px]"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TextField.Slot side="right">
                        <X
                          size={16}
                          className="cursor-pointer hover:text-puntt-blue-5"
                          onClick={() => {
                            setEditingUrlForFile(null);
                            setUrlInput("");
                          }}
                        />
                        <Button
                          variant="ghost"
                          disabled={!!urlInput.length && !isValidUrl(urlInput)}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newFiles = files.map((f) => {
                              if (f.source === file.source) {
                                return { ...f, publicUrl: urlInput };
                              }
                              return f;
                            });
                            onSave(newFiles);
                            setEditingUrlForFile(null);
                            setUrlInput("");
                          }}
                          className="cursor-pointer hover:text-puntt-blue-5"
                        >
                          <Check size={16} />
                        </Button>
                      </TextField.Slot>
                    </TextField.Root>
                  </Tooltip>
                </div>
              </form>
            ) : (
              file.publicUrl || "Add URL"
            )}
          </Text>
        </Flex>
      </Button>
    );
  }

  const handlePersonaChange = async (
    personaName: string,
    field: "enabled" | "default",
    value: boolean,
  ) => {
    try {
      const payload = {
        name: personaName,
        [field]: value,
      };
      if (field == "default" && value) {
        payload.enabled = true;
      }
      await updatePersonaMutation.mutateAsync(payload, {
        onSuccess() {
          notify({
            title: "Persona settings updated",
            variant: "success",
          });
          query.refetch();
          posthog.capture("Persona Settings Changed", {
            description: "Persona settings updated",
            personaName,
            field,
            value,
          });
        },
      });
    } catch (error) {
      notify({
        title: "Error updating persona settings",
        message: String(error),
        variant: "error",
      });
    }
  };

  return (
    <div
      className={cx("mb-4 overflow-auto px-8 py-2")}
      data-testid="knowledge-settings-page"
    >
      <Flex direction="column" className="px-2 py-4">
        <Heading
          size="8"
          mb="4"
          className="font-sans"
          data-testid="knowledge-settings-title"
        >
          Knowledge Settings
        </Heading>
        <Text
          size="3"
          color="gray"
          data-testid="knowledge-settings-description"
        >
          Guidelines are used by Puntt AI to generate feedback for AI Reviews.
        </Text>
      </Flex>

      <Tabs.Root defaultValue="ai-review">
        <Tabs.List>
          <Tabs.Trigger value="ai-review" data-testid="ai-review-tab">
            AI Review
          </Tabs.Trigger>
          <Tabs.Trigger value="askpuntt" data-testid="askpuntt-tab">
            AskPuntt
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="ai-review">
          <Grid gap="4" px="2" py="4">
            <Grid gap="2">
              <Text
                size="5"
                weight="bold"
                data-testid="ai-personas-section-title"
              >
                AI Reviewer Personas
              </Text>

              <Text size="3" data-testid="ai-personas-section-description">
                When you run an AI Review, these personas give feedback on
                different aspects of the asset(s).
              </Text>
            </Grid>

            <Table.Root
              variant="surface"
              className="w-full min-w-[300px] max-w-[800px]"
              data-testid="ai-personas-table"
            >
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Persona</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-[120px] text-center">
                    <Flex align="center" justify="center" gap="1">
                      Enabled
                      <Tooltip content="If enabled, this persona will be available for users to select when running an AI Review. It won't be included by default, but users can opt in to include it.">
                        <Question
                          size={16}
                          weight="fill"
                          className="text-gray-500 inline-block align-middle"
                        />
                      </Tooltip>
                    </Flex>
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className="w-[120px] text-center">
                    <Flex align="center" justify="center" gap="1">
                      Default
                      <Tooltip content="If set as default, this persona will automatically be included in every AI Review unless a user manually deselects it.">
                        <Question
                          size={16}
                          weight="fill"
                          className="text-gray-500 inline-block align-middle"
                        />
                      </Tooltip>
                    </Flex>
                  </Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {query.data?.aiPersonas.map((persona) => (
                  <Table.Row key={persona.name}>
                    <Table.Cell>
                      <Flex gap="2" align="center">
                        {renderPersonaAvatar(persona.name)}
                        {personaName(persona.name)}
                      </Flex>
                    </Table.Cell>
                    <Table.Cell className="text-center">
                      <Checkbox
                        checked={persona.enabled}
                        defaultChecked={persona.enabled || persona.default}
                        onCheckedChange={(checked) =>
                          handlePersonaChange(
                            persona.name!,
                            "enabled",
                            !!checked,
                          )
                        }
                        data-testid={`ai-persona-enabled-${persona.name}`}
                      />
                    </Table.Cell>
                    <Table.Cell className="text-center">
                      <Checkbox
                        checked={persona.default}
                        defaultChecked={persona.default}
                        onCheckedChange={(checked) =>
                          handlePersonaChange(
                            persona.name!,
                            "default",
                            !!checked,
                          )
                        }
                        data-testid={`ai-persona-default-${persona.name}`}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Grid>

          <div className="h-4" />

          <Grid
            columns={{ initial: "1", md: "1" }}
            gap="6"
            className="mb-16 px-2"
          >
            <Flex direction="column" gap="4">
              <Text
                size="7"
                weight="bold"
                mb="2"
                data-testid="guidelines-section-title"
              >
                Guidelines
              </Text>
              <Text
                size="3"
                mb="4"
                data-testid="guidelines-section-description"
              >
                Add or edit guidelines that Puntt AI will use to generate
                feedback. This could include brand standards, compliance
                requirements, visual guidelines, copywriting and style manuals,
                or anything else that describes what is or is not allowed.
              </Text>

              <Flex direction="column" gap="4">
                {/* Files Section */}
                <Text size="5" weight="bold" data-testid="files-section-title">
                  Files
                </Text>
                <input
                  type="file"
                  className="hidden"
                  ref={fileInputRef}
                  accept=".pdf,.doc, .docx, .xls, .xlsx, .ppt, .pptx, .odt, .ods, .odp, .odg, .odf, .txt, .rtf"
                  onChange={handleGuidelineUpload}
                  multiple
                  data-testid="knowledge-settings-files-input"
                />

                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className={cx(
                    "flex h-8 w-1/2 items-center justify-center gap-2 rounded-md border border-[rgba(0,50,255,0.47)] px-3 md:w-fit",
                    "hover:bg-puntt-neutral-gray-4",
                    "active:text-puntt-neutral-gray-5",
                  )}
                  data-testid="knowledge-settings-add-files-button"
                >
                  {isUploading ? (
                    <Spinner size={20} className="animate-spin" />
                  ) : (
                    <>
                      <Plus size={20} />
                      <Text size="2">
                        {fileInputRef.current?.files?.[0]?.name ?? "Add Files"}
                      </Text>
                    </>
                  )}
                </Button>

                <ul
                  className="space-y-2"
                  data-testid="knowledge-settings-files-list"
                >
                  {files
                    ?.filter((file) => {
                      const ext = getFileExtension(file);
                      return [
                        "pdf",
                        "doc",
                        "docx",
                        "xls",
                        "xlsx",
                        "ppt",
                        "pptx",
                        "odt",
                        "ods",
                        "odp",
                        "odg",
                        "odf",
                        "txt",
                        "rtf",
                      ].includes(ext);
                    })
                    .map(
                      (file: {
                        source: string;
                        originalSource?: string;
                        name?: string;
                        publicUrl?: string;
                      }) => {
                        const fileName =
                          file?.originalSource ||
                          file?.name ||
                          file?.source?.split("/").pop();
                        return (
                          <li
                            key={file?.source}
                            className="group flex-wrap items-center justify-start"
                          >
                            <Dialog.Root>
                              <Dialog.Trigger>
                                <Button
                                  variant="ghost"
                                  className="max-w-full truncate py-0 text-puntt-blue-12 underline decoration-puntt-blue-12 underline-offset-4"
                                >
                                  {getFileIcon(fileName || "")}
                                  <span className="truncate">{fileName}</span>
                                </Button>
                              </Dialog.Trigger>
                              <Dialog.Content
                                style={{
                                  width: "90vw",
                                  maxWidth: "80rem",
                                  height: "90vh",
                                  maxHeight: "60rem",
                                }}
                              >
                                <Flex
                                  justify="between"
                                  align="center"
                                  gap="4"
                                  mb="4"
                                >
                                  <Dialog.Title className="mb-0">
                                    {fileName}
                                  </Dialog.Title>
                                  <Dialog.Description className="hidden">
                                    File preview
                                  </Dialog.Description>
                                  <Button
                                    className="ml-auto flex gap-1 hover:bg-puntt-accent-10 hover:text-base-white"
                                    variant="outline"
                                    size="1"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      downloadAsset(file?.source, fileName);
                                    }}
                                  >
                                    <Text>Download</Text>
                                    <DownloadSimple width={16} height={16} />
                                  </Button>
                                  <Dialog.Close>
                                    <Button
                                      type="button"
                                      variant="soft"
                                      color="gray"
                                    >
                                      Close
                                    </Button>
                                  </Dialog.Close>
                                </Flex>
                                <Preview url={file?.source} />
                              </Dialog.Content>
                            </Dialog.Root>

                            <AddUrlButton
                              file={file}
                              files={files}
                              editingUrlForFile={editingUrlForFile}
                              setEditingUrlForFile={setEditingUrlForFile}
                              startingUrl={file.publicUrl || ""}
                              onSave={(newFiles) => {
                                setFiles(newFiles);
                                mutation.mutate(
                                  {
                                    description: guidelines,
                                    files: newFiles,
                                    templates: templates,
                                  },
                                  {
                                    onSuccess() {
                                      notify({
                                        title: "URL added",
                                        message: "",
                                        variant: "success",
                                      });
                                      setEditingUrlForFile(null);
                                    },
                                    onError() {
                                      notify({
                                        title: "Error adding URL",
                                        message: "Please try again",
                                        variant: "error",
                                      });
                                    },
                                  },
                                );
                              }}
                            />

                            <Button
                              variant="ghost"
                              onClick={() => handleDelete(file)}
                              className="opacity-0 group-hover:opacity-100"
                              data-testid={`knowledge-settings-delete-file-${file.source.split("/").pop()}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </li>
                        );
                      },
                    )}
                </ul>

                {/* Type Guidelines Section */}
                <Flex direction="column" gap="2" className="mt-4">
                  <Flex align="center" gap="2">
                    <Text weight="medium" data-testid="guidelines-text-label">
                      Or Type Guidelines
                    </Text>
                    {isTyping && (
                      <Text
                        size="2"
                        color="gray"
                        data-testid="guidelines-typing-indicator"
                      >
                        <Spinner size={12} className="animate-spin" />
                      </Text>
                    )}
                    {isSaved && (
                      <Text
                        size="2"
                        color="green"
                        data-testid="guidelines-saved-indicator"
                      >
                        Saved
                      </Text>
                    )}
                  </Flex>
                  <TextArea
                    placeholder="Enter your guidelines here"
                    value={guidelines}
                    onChange={handleInput}
                    rows={8}
                    name="guidelines"
                    data-testid="knowledge-settings-guidelines-textarea"
                  />
                </Flex>

                {/* Templates Section */}
                <Text
                  size="5"
                  weight="bold"
                  data-testid="templates-section-title"
                  className="mt-4"
                >
                  Templates
                </Text>
                <input
                  type="file"
                  className="hidden"
                  ref={templateInputRef}
                  accept="*/*"
                  onChange={handleTemplateUpload}
                  multiple
                  data-testid="knowledge-settings-template-input"
                />

                <Button
                  variant="outline"
                  onClick={() => templateInputRef.current?.click()}
                  className={cx(
                    "flex h-8 w-1/2 items-center justify-center gap-2 rounded-md border border-[rgba(0,50,255,0.47)] px-3 md:w-fit",
                    "hover:bg-puntt-neutral-gray-4",
                    "active:text-puntt-neutral-gray-5",
                  )}
                  data-testid="knowledge-settings-add-templates-button"
                >
                  {isUploadingTemplates ? (
                    <Spinner size={20} className="animate-spin" />
                  ) : (
                    <>
                      <Plus size={20} />
                      <Text size="2">
                        {templateInputRef.current?.files?.[0]?.name ??
                          "Add Templates"}
                      </Text>
                    </>
                  )}
                </Button>

                <ul
                  className="space-y-2"
                  data-testid="knowledge-settings-templates-list"
                >
                  {templates?.map(
                    (file: {
                      source: string;
                      originalSource?: string;
                      name?: string;
                      publicUrl?: string;
                    }) => {
                      const fileName =
                        file?.originalSource ||
                        file?.name ||
                        file?.source?.split("/").pop();
                      return (
                        <li
                          key={file?.source}
                          className="group flex-wrap items-center justify-start"
                        >
                          <Dialog.Root>
                            <Dialog.Trigger>
                              <Button
                                variant="ghost"
                                className="max-w-full truncate py-0 text-puntt-blue-12 underline decoration-puntt-blue-12 underline-offset-4"
                              >
                                {getFileIcon(fileName || "")}
                                <span className="truncate">{fileName}</span>
                              </Button>
                            </Dialog.Trigger>
                            <Dialog.Content
                              style={{
                                width: "90vw",
                                maxWidth: "80rem",
                                height: "90vh",
                                maxHeight: "60rem",
                              }}
                            >
                              <Flex
                                justify="between"
                                align="center"
                                gap="4"
                                mb="4"
                              >
                                <Dialog.Title className="mb-0">
                                  {fileName}
                                </Dialog.Title>
                                <Dialog.Description className="hidden">
                                  File preview
                                </Dialog.Description>
                                <Button
                                  className="ml-auto flex gap-1 hover:bg-puntt-accent-10 hover:text-base-white"
                                  variant="outline"
                                  size="1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    downloadAsset(file?.source, fileName);
                                  }}
                                >
                                  <Text>Download</Text>
                                  <DownloadSimple width={16} height={16} />
                                </Button>
                                <Dialog.Close>
                                  <Button
                                    type="button"
                                    variant="soft"
                                    color="gray"
                                  >
                                    Close
                                  </Button>
                                </Dialog.Close>
                              </Flex>
                              <Preview url={file?.source} />
                            </Dialog.Content>
                          </Dialog.Root>

                          <Button
                            variant="ghost"
                            onClick={() => handleDeleteTemplate(file)}
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <Trash size={16} />
                          </Button>
                        </li>
                      );
                    },
                  )}
                </ul>
              </Flex>
            </Flex>
          </Grid>
        </Tabs.Content>

        <Tabs.Content value="askpuntt">
          <Grid gap="2" px="2" py="4" data-testid="askpuntt-enabled-section">
            <Flex align="center" justify="between" gap="4">
              <Flex direction="column" gap="1">
                <Text size="5" weight="bold">
                  AskPuntt Access
                </Text>
                <Text size="2" color="gray">
                  When enabled, every user in your organization can access
                  AskPuntt. When disabled, AskPuntt is hidden from regular
                  users.
                </Text>
              </Flex>
              <Switch
                size="3"
                checked={query.data?.askPunttEnabled ?? false}
                onCheckedChange={handleAskPunttEnabledChange}
                disabled={updateAskPunttEnabledMutation.isPending}
                data-testid="askpuntt-enabled-toggle"
              />
            </Flex>
          </Grid>
          <Separator size="4" />
          <AskPunttSkillSettings
            skills={query.data?.askPunttSkills ?? []}
            onUpdate={handleSkillUpdate}
          />
          <AskPunttConnectorSettings
            connectors={query.data?.askPunttConnectors ?? []}
            onUpdate={handleConnectorUpdate}
          />
          <CustomWorkflowSettings
            workflows={query.data?.customWorkflows ?? []}
            onCreate={async (body) => {
              await createWorkflowMutation.mutateAsync(body);
              notify({ title: "Custom skill created", variant: "success" });
            }}
            onUpdate={async (body) => {
              await updateWorkflowMutation.mutateAsync(body);
              notify({ title: "Custom skill updated", variant: "success" });
            }}
            onDelete={async (body) => {
              await deleteWorkflowMutation.mutateAsync(body);
              notify({ title: "Custom skill deleted", variant: "success" });
            }}
          />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

export function AskPunttSkillSettings({
  skills,
  onUpdate,
}: {
  skills: EnterpriseAskPunttSkill[];
  onUpdate: (payload: {
    skillId: string;
    enabled?: boolean;
    customInstructions?: string;
  }) => Promise<void>;
}) {
  const saveTimeoutRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const [localInstructions, setLocalInstructions] = useState<
    Record<string, string>
  >({});
  const [skillDocuments, setSkillDocuments] = useState<
    Record<string, SkillDocument[]>
  >({});
  const [skillRequiredInputsDesc, setSkillRequiredInputsDesc] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    return () => {
      for (const timeout of Object.values(saveTimeoutRefs.current)) {
        clearTimeout(timeout);
      }
    };
  }, []);

  const prevSkillsRef = useRef<Record<string, string>>({});
  useEffect(() => {
    setLocalInstructions((prev) => {
      const next: Record<string, string> = {};
      for (const skill of skills) {
        const serverValue = skill.customInstructions ?? "";
        const previousServerValue = prevSkillsRef.current[skill.skillId];
        if (
          prev[skill.skillId] === previousServerValue ||
          prev[skill.skillId] === undefined
        ) {
          next[skill.skillId] = serverValue;
        } else {
          next[skill.skillId] = prev[skill.skillId];
        }
      }
      const serverSnapshot: Record<string, string> = {};
      for (const skill of skills) {
        serverSnapshot[skill.skillId] = skill.customInstructions ?? "";
      }
      prevSkillsRef.current = serverSnapshot;
      return next;
    });

    setSkillRequiredInputsDesc((prev) => {
      const next: Record<string, string> = { ...prev };
      for (const skill of skills) {
        if (next[skill.skillId] === undefined) {
          next[skill.skillId] = skill.requiredInputsDescription ?? "";
        }
      }
      return next;
    });

    setSkillDocuments((prev) => {
      const next: Record<string, SkillDocument[]> = { ...prev };
      for (const skill of skills) {
        if (next[skill.skillId] === undefined) {
          next[skill.skillId] = (skill.documents ?? []) as SkillDocument[];
        }
      }
      return next;
    });
  }, [skills]);

  const handleInstructionsChange = useCallback(
    (skillId: string, value: string) => {
      setLocalInstructions((prev) => ({ ...prev, [skillId]: value }));

      if (saveTimeoutRefs.current[skillId]) {
        clearTimeout(saveTimeoutRefs.current[skillId]);
      }

      saveTimeoutRefs.current[skillId] = setTimeout(() => {
        onUpdate({ skillId, customInstructions: value });
      }, 2000);
    },
    [onUpdate],
  );

  return (
    <Grid gap="4" px="2" py="4">
      <Grid gap="2">
        <Text size="5" weight="bold">
          AskPuntt Skill Settings
        </Text>
        <Text size="3">
          Configure which AskPuntt skills are available and add custom
          instructions, reference documents, and required inputs for each skill.
        </Text>
      </Grid>

      <Table.Root
        variant="surface"
        className="w-full min-w-[300px]"
        data-testid="askpuntt-skills-table"
      >
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell className="w-[160px]">
              Skill
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="w-[90px] text-center">
              Enabled
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Configuration</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {skills.map((skill) => (
            <Table.Row key={skill.skillId}>
              <Table.Cell className="pt-4 align-top font-medium">
                {skill.name}
              </Table.Cell>
              <Table.Cell className="pt-4 text-center align-top">
                <Checkbox
                  checked={skill.enabled}
                  onCheckedChange={(checked) =>
                    onUpdate({ skillId: skill.skillId, enabled: !!checked })
                  }
                  data-testid={`askpuntt-skill-enabled-${skill.skillId}`}
                />
              </Table.Cell>
              <Table.Cell>
                <Grid columns="2" gap="4" py="2">
                  <Flex direction="column" gap="1">
                    <Text size="2" weight="medium">
                      Custom Instructions
                    </Text>
                    <TextArea
                      placeholder="Add custom instructions for this skill..."
                      value={localInstructions[skill.skillId] ?? ""}
                      onChange={(e) =>
                        handleInstructionsChange(skill.skillId, e.target.value)
                      }
                      rows={4}
                      data-testid={`askpuntt-skill-instructions-${skill.skillId}`}
                    />
                  </Flex>

                  <Flex direction="column" gap="3">
                    <SkillDocumentsPanel
                      skillId={skill.skillId}
                      skillType="builtin"
                      documents={skillDocuments[skill.skillId] ?? []}
                      onDocumentsChange={(docs) =>
                        setSkillDocuments((prev) => ({
                          ...prev,
                          [skill.skillId]: docs,
                        }))
                      }
                    />
                    <Separator size="4" />
                    <RequiredInputsEditor
                      skillId={skill.skillId}
                      skillType="builtin"
                      description={skillRequiredInputsDesc[skill.skillId] ?? ""}
                      onDescriptionChange={(desc) =>
                        setSkillRequiredInputsDesc((prev) => ({
                          ...prev,
                          [skill.skillId]: desc,
                        }))
                      }
                    />
                  </Flex>
                </Grid>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Grid>
  );
}

function AskPunttConnectorSettings({
  connectors,
  onUpdate,
}: {
  connectors: EnterpriseAskPunttConnector[];
  onUpdate: (payload: {
    connectorId: string;
    enabled?: boolean;
  }) => Promise<void>;
}) {
  return (
    <Grid gap="4" px="2" py="4">
      <Grid gap="2">
        <Text size="5" weight="bold">
          AskPuntt Integrations
        </Text>
        <Text size="3">
          Configure which integrations are available for AskPuntt chats to
          search. Integrations must be connected in Composio before they can be
          enabled.
        </Text>
      </Grid>

      <Table.Root
        variant="surface"
        className="w-full min-w-[300px] max-w-[800px]"
        data-testid="askpuntt-connectors-table"
      >
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell className="w-[200px]">
              Integration
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="w-[160px] text-center">
              Status
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell className="w-[100px] text-center">
              Enabled
            </Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {connectors.map((connector) => (
            <Table.Row key={connector.connectorId}>
              <Table.Cell className="font-medium">{connector.name}</Table.Cell>
              <Table.Cell className="text-center">
                <Flex align="center" justify="center" gap="2">
                  <span
                    className={cx(
                      "inline-block h-2 w-2 rounded-full",
                      connector.connected ? "bg-green-500" : "bg-gray-400",
                    )}
                  />
                  <Text size="2" color={connector.connected ? "green" : "gray"}>
                    {connector.connected ? "Connected" : "Not Connected"}
                  </Text>
                </Flex>
              </Table.Cell>
              <Table.Cell className="text-center">
                <Tooltip
                  content={
                    connector.connected
                      ? "Toggle this integration for AskPuntt chats"
                      : "Connect this integration in Composio first"
                  }
                >
                  <span>
                    <Checkbox
                      checked={connector.enabled}
                      disabled={!connector.connected}
                      onCheckedChange={(checked) =>
                        onUpdate({
                          connectorId: connector.connectorId,
                          enabled: !!checked,
                        })
                      }
                      data-testid={`askpuntt-connector-enabled-${connector.connectorId}`}
                    />
                  </span>
                </Tooltip>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Grid>
  );
}

export function CustomWorkflowModal({
  open,
  onOpenChange,
  onSave,
  workflow,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    name: string;
    trigger: string;
    steps: string;
    toolAccess?: string[];
    webSearchEnabled?: boolean;
    webSearchDomains?: string[];
  }) => Promise<void>;
  workflow?: EnterpriseCustomWorkflow;
}) {
  const [name, setName] = useState(workflow?.name ?? "");
  const [trigger, setTrigger] = useState(workflow?.trigger ?? "");
  const [steps, setSteps] = useState(workflow?.steps ?? "");
  const [toolAccess, setToolAccess] = useState<string[]>(
    workflow?.toolAccess ?? [],
  );
  const [webSearchEnabled, setWebSearchEnabled] = useState(
    workflow?.webSearchEnabled ?? false,
  );
  const [webSearchDomains, setWebSearchDomains] = useState(
    workflow?.webSearchDomains?.join(", ") ?? "",
  );
  const [documents, setDocuments] = useState<SkillDocument[]>(
    (workflow?.documents ?? []) as SkillDocument[],
  );
  const [requiredInputsDesc, setRequiredInputsDesc] = useState(
    workflow?.requiredInputsDescription ?? "",
  );
  const [saving, setSaving] = useState(false);

  const isSaved = !!workflow?.workflowId;

  useEffect(() => {
    if (open) {
      setName(workflow?.name ?? "");
      setTrigger(workflow?.trigger ?? "");
      setSteps(workflow?.steps ?? "");
      setToolAccess(workflow?.toolAccess ?? []);
      setWebSearchEnabled(workflow?.webSearchEnabled ?? false);
      setWebSearchDomains(workflow?.webSearchDomains?.join(", ") ?? "");
      setDocuments((workflow?.documents ?? []) as SkillDocument[]);
      setRequiredInputsDesc(workflow?.requiredInputsDescription ?? "");
    }
  }, [open, workflow]);

  const TOOL_OPTIONS = [
    { id: "web_search", label: "Web Search" },
    { id: "enterprise_context", label: "Enterprise Context" },
    { id: "ticket_query", label: "Ticket Query" },
    { id: "brand_guidelines_kb", label: "Brand Guidelines KB" },
    { id: "create_ticket", label: "Create Ticket" },
    { id: "wrike", label: "Wrike" },
    { id: "asana", label: "Asana" },
    { id: "sharepoint", label: "SharePoint" },
    { id: "google_drive", label: "Google Drive" },
    { id: "figma", label: "Figma" },
  ];

  const toggleTool = (toolId: string) => {
    setToolAccess((prev) =>
      prev.includes(toolId)
        ? prev.filter((t) => t !== toolId)
        : [...prev, toolId],
    );
  };

  const handleSave = async () => {
    if (!name.trim() || !trigger.trim() || !steps.trim()) return;
    setSaving(true);
    try {
      const domains = webSearchDomains
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);
      await onSave({
        name: name.trim(),
        trigger: trigger.trim(),
        steps: steps.trim(),
        toolAccess: toolAccess.length > 0 ? toolAccess : undefined,
        webSearchEnabled: webSearchEnabled || undefined,
        webSearchDomains: domains.length > 0 ? domains : undefined,
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="560px">
        <Dialog.Title>
          {workflow ? "Edit Custom Skill" : "Add Custom Skill"}
        </Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Define a custom skill that AskPuntt will detect and use automatically.
        </Dialog.Description>

        <Flex direction="column" gap="4">
          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              Name
            </Text>
            <TextField.Root
              placeholder="e.g. Claims Review"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              Trigger
            </Text>
            <TextArea
              placeholder="When the user asks about..."
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              rows={2}
            />
          </Flex>

          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              Steps
            </Text>
            <TextArea
              placeholder="The workflow should..."
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              rows={4}
            />
          </Flex>

          <Flex direction="column" gap="2">
            <Text size="2" weight="medium">
              Tools
            </Text>
            <Flex wrap="wrap" gap="2">
              {TOOL_OPTIONS.map((tool) => (
                <Flex key={tool.id} align="center" gap="1">
                  <Checkbox
                    checked={toolAccess.includes(tool.id)}
                    onCheckedChange={() => toggleTool(tool.id)}
                  />
                  <Text size="1">{tool.label}</Text>
                </Flex>
              ))}
            </Flex>
          </Flex>

          {toolAccess.includes("web_search") && (
            <Flex direction="column" gap="1">
              <Flex align="center" gap="1">
                <Checkbox
                  checked={webSearchEnabled}
                  onCheckedChange={(checked) => setWebSearchEnabled(!!checked)}
                />
                <Text size="2" weight="medium">
                  Restrict web search domains
                </Text>
              </Flex>
              {webSearchEnabled && (
                <TextField.Root
                  placeholder="e.g. fda.gov, eur-lex.europa.eu"
                  value={webSearchDomains}
                  onChange={(e) => setWebSearchDomains(e.target.value)}
                />
              )}
            </Flex>
          )}

          <Separator size="4" />

          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              Reference Documents
            </Text>
            {isSaved ? (
              <SkillDocumentsPanel
                skillId={workflow!.workflowId}
                skillType="custom"
                documents={documents}
                onDocumentsChange={setDocuments}
              />
            ) : (
              <Text size="2" color="gray">
                Save the skill first to upload reference documents.
              </Text>
            )}
          </Flex>

          <Separator size="4" />

          <Flex direction="column" gap="1">
            <Text size="2" weight="medium">
              Required Inputs
            </Text>
            {isSaved ? (
              <RequiredInputsEditor
                skillId={workflow!.workflowId}
                skillType="custom"
                description={requiredInputsDesc}
                onDescriptionChange={setRequiredInputsDesc}
              />
            ) : (
              <Text size="2" color="gray">
                Save the skill first to configure required inputs.
              </Text>
            )}
          </Flex>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            onClick={handleSave}
            disabled={
              !name.trim() || !trigger.trim() || !steps.trim() || saving
            }
          >
            {saving ? <Spinner /> : null}
            {workflow ? "Save Changes" : "Create Skill"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function CustomWorkflowSettings({
  workflows,
  onCreate,
  onUpdate,
  onDelete,
}: {
  workflows: EnterpriseCustomWorkflow[];
  onCreate: (body: {
    name: string;
    trigger: string;
    steps: string;
    toolAccess?: string[];
    webSearchEnabled?: boolean;
    webSearchDomains?: string[];
  }) => Promise<void>;
  onUpdate: (body: {
    workflowId: string;
    name?: string;
    trigger?: string;
    steps?: string;
    toolAccess?: string[];
    webSearchEnabled?: boolean;
    webSearchDomains?: string[];
    enabled?: boolean;
  }) => Promise<void>;
  onDelete: (body: { workflowId: string }) => Promise<void>;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<
    EnterpriseCustomWorkflow | undefined
  >();
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  return (
    <Grid gap="4" px="2" py="4">
      <Flex justify="between" align="center">
        <Grid gap="2">
          <Text size="5" weight="bold">
            Custom Skills
          </Text>
          <Text size="3">
            Create custom skills that AskPuntt will automatically detect and use
            based on trigger conditions.
          </Text>
        </Grid>
        <Button
          onClick={() => {
            setEditingWorkflow(undefined);
            setModalOpen(true);
          }}
          data-testid="add-custom-workflow-btn"
        >
          <Plus size={14} /> Add Custom Skill
        </Button>
      </Flex>

      {workflows.length > 0 && (
        <Table.Root
          variant="surface"
          className="w-full min-w-[300px]"
          data-testid="custom-workflows-table"
        >
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell className="w-[180px]">
                Name
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Trigger</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="w-[100px] text-center">
                Enabled
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell className="w-[100px] text-center">
                Actions
              </Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {workflows.map((wf) => (
              <Table.Row key={wf.workflowId}>
                <Table.Cell className="font-medium">{wf.name}</Table.Cell>
                <Table.Cell>
                  <Text size="2" className="line-clamp-2">
                    {wf.trigger}
                  </Text>
                </Table.Cell>
                <Table.Cell className="text-center">
                  <Checkbox
                    checked={wf.enabled}
                    onCheckedChange={(checked) =>
                      onUpdate({
                        workflowId: wf.workflowId,
                        enabled: !!checked,
                      })
                    }
                  />
                </Table.Cell>
                <Table.Cell className="text-center">
                  <Flex gap="2" justify="center">
                    <Button
                      variant="ghost"
                      size="1"
                      onClick={() => {
                        setEditingWorkflow(wf);
                        setModalOpen(true);
                      }}
                    >
                      <PencilSimple size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="1"
                      color="red"
                      onClick={() => setDeleteConfirmId(wf.workflowId)}
                    >
                      <Trash size={14} />
                    </Button>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}

      <CustomWorkflowModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        workflow={editingWorkflow}
        onSave={async (data) => {
          if (editingWorkflow) {
            await onUpdate({ workflowId: editingWorkflow.workflowId, ...data });
          } else {
            await onCreate(data);
          }
        }}
      />

      <Dialog.Root
        open={deleteConfirmId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
      >
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Delete Custom Skill</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Are you sure you want to delete this skill? This action cannot be
            undone.
          </Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              color="red"
              onClick={async () => {
                if (deleteConfirmId) {
                  await onDelete({ workflowId: deleteConfirmId });
                  setDeleteConfirmId(null);
                }
              }}
            >
              Delete
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Grid>
  );
}
